import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'sonner'

export interface Assinatura {
  id: string
  documento_tipo: string
  documento_id: string
  user_id: string
  papel: string
  status: 'pendente' | 'assinado'
  assinado_em: string | null
  ordem: number
  criado_por: string
  created_at: string
  perfil?: { nome: string; cargo: string | null; perfil: string }
}

export interface AssinaturaNotificacao {
  id: string
  documento_tipo: string
  documento_id: string
  titulo: string
  mensagem: string | null
  lida: boolean
  created_at: string
}

const PAPEIS = [
  { key: 'responsavel_tecnico', label: 'Responsável Técnico' },
  { key: 'encarregado', label: 'Encarregado' },
  { key: 'aprovador', label: 'Aprovador' },
  { key: 'assinante', label: 'Assinante' },
]

export { PAPEIS }

export function useDocumentoAssinaturas(documentoTipo: string, documentoId: string | null) {
  const { user } = useAuth()
  const [assinaturas, setAssinaturas] = useState<Assinatura[]>([])
  const [loading, setLoading] = useState(false)
  const [signing, setSigning] = useState(false)

  const load = useCallback(async () => {
    if (!documentoId) { setAssinaturas([]); return }
    setLoading(true)
    const { data } = await supabase
      .from('documento_assinaturas')
      .select('*')
      .eq('documento_tipo', documentoTipo)
      .eq('documento_id', documentoId)
      .order('ordem')
    
    if (data && data.length > 0) {
      // Load profile names
      const userIds = [...new Set(data.map((a: any) => a.user_id))]
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, nome, cargo, perfil')
        .in('id', userIds)
      
      const profileMap = new Map((profiles || []).map((p: any) => [p.id, p]))
      const enriched = data.map((a: any) => ({
        ...a,
        perfil: profileMap.get(a.user_id) || { nome: 'Usuário', cargo: null, perfil: '' },
      }))
      setAssinaturas(enriched as Assinatura[])
    } else {
      setAssinaturas([])
    }
    setLoading(false)
  }, [documentoTipo, documentoId])

  useEffect(() => { load() }, [load])

  const addAssinantes = useCallback(async (
    assinantes: Array<{ user_id: string; papel: string }>,
    documentoTituloParaNotificacao?: string,
  ) => {
    if (!documentoId || !user) return false
    try {
      const rows = assinantes.map((a, i) => ({
        company_id: user.company_id,
        documento_tipo: documentoTipo,
        documento_id: documentoId,
        user_id: a.user_id,
        papel: a.papel,
        ordem: i + 1,
        criado_por: user.id,
      }))
      const { error } = await supabase.from('documento_assinaturas').insert(rows)
      if (error) throw error

      // Create notifications
      const notifs = assinantes.map(a => ({
        company_id: user.company_id,
        user_id: a.user_id,
        documento_tipo: documentoTipo,
        documento_id: documentoId,
        titulo: '✍️ Assinatura pendente',
        mensagem: `Você foi designado para assinar: ${documentoTituloParaNotificacao || documentoTipo.toUpperCase()}`,
      }))
      await supabase.from('assinatura_notificacoes').insert(notifs)

      await load()
      toast.success('Assinantes adicionados')
      return true
    } catch (err: any) {
      toast.error(err.message)
      return false
    }
  }, [documentoId, documentoTipo, user, load])

  const assinar = useCallback(async (assinaturaBase64?: string) => {
    if (!documentoId || !user) return false
    setSigning(true)
    try {
      let assinatura_url: string | null = null

      // Upload signature image if provided
      if (assinaturaBase64) {
        const { uploadAssinatura } = await import('@/lib/upload-assinaturas')
        assinatura_url = await uploadAssinatura('documento_assinaturas', documentoId, user.id, assinaturaBase64, user.company_id)
        // [log removido — dados sensíveis]
      }

      const payload: Record<string, unknown> = {
        status: 'assinado',
        assinado_em: new Date().toISOString(),
        ...(assinatura_url ? { assinatura_url } : {}),
      }
      // [log removido — dados sensíveis]

      const { error } = await supabase
        .from('documento_assinaturas')
        .update(payload)
        .eq('documento_tipo', documentoTipo)
        .eq('documento_id', documentoId)
        .eq('user_id', user.id)
        .eq('status', 'pendente')
      if (error) throw error

      await load()

      // Check if all signed → notify creator
      const { data: remaining } = await supabase
        .from('documento_assinaturas')
        .select('id')
        .eq('documento_tipo', documentoTipo)
        .eq('documento_id', documentoId)
        .eq('status', 'pendente')
      
      if (remaining && remaining.length === 0) {
        // All signed - get creator to notify
        const { data: firstSig } = await supabase
          .from('documento_assinaturas')
          .select('criado_por')
          .eq('documento_tipo', documentoTipo)
          .eq('documento_id', documentoId)
          .limit(1)
          .single()
        
        if (firstSig && firstSig.criado_por !== user.id) {
          await supabase.from('assinatura_notificacoes').insert({
            company_id: user.company_id,
            user_id: firstSig.criado_por,
            documento_tipo: documentoTipo,
            documento_id: documentoId,
            titulo: '✅ Todas as assinaturas coletadas',
            mensagem: `Todos os assinantes concluíram: ${documentoTipo.toUpperCase()}`,
          })
        }
      }

      toast.success('Documento assinado com sucesso!')
      return true
    } catch (err: any) {
      toast.error(err.message)
      return false
    } finally {
      setSigning(false)
    }
  }, [documentoId, documentoTipo, user, load])

  const removeAssinante = useCallback(async (assinaturaId: string) => {
    const { error } = await supabase.from('documento_assinaturas').delete().eq('id', assinaturaId).eq('company_id', user?.company_id)
    if (error) toast.error(error.message)
    else { toast.success('Assinante removido'); load() }
  }, [load, user?.company_id])

  const totalAssinaturas = assinaturas.length
  const assinaturasFeitas = assinaturas.filter(a => a.status === 'assinado').length
  const progresso = totalAssinaturas > 0 ? Math.round((assinaturasFeitas / totalAssinaturas) * 100) : 0
  const todasAssinadas = totalAssinaturas > 0 && assinaturasFeitas === totalAssinaturas
  const temAssinaturaPendente = user ? assinaturas.some(a => a.user_id === user.id && a.status === 'pendente') : false
  const jaAssinou = user ? assinaturas.some(a => a.user_id === user.id && a.status === 'assinado') : false
  const temAlgumaAssinatura = assinaturasFeitas > 0

  // Document status derived from signatures
  const statusDocumento = totalAssinaturas === 0
    ? 'rascunho'
    : todasAssinadas
      ? 'finalizado'
      : temAlgumaAssinatura
        ? 'parcialmente_assinado'
        : 'aguardando_assinaturas'

  return {
    assinaturas,
    loading,
    signing,
    addAssinantes,
    assinar,
    removeAssinante,
    load,
    totalAssinaturas,
    assinaturasFeitas,
    progresso,
    todasAssinadas,
    temAssinaturaPendente,
    jaAssinou,
    temAlgumaAssinatura,
    statusDocumento,
  }
}

// Hook for notifications badge
export function useAssinaturaNotificacoes() {
  const { user } = useAuth()
  const [notificacoes, setNotificacoes] = useState<AssinaturaNotificacao[]>([])
  const [count, setCount] = useState(0)

  const load = useCallback(async () => {
    if (!user) return
    const { data } = await supabase
      .from('assinatura_notificacoes')
      .select('*')
      .eq('user_id', user.id)
      .eq('lida', false)
      .order('created_at', { ascending: false })
      .limit(20)
    setNotificacoes((data || []) as AssinaturaNotificacao[])
    setCount((data || []).length)
  }, [user])

  // Ref para usar sempre a versão mais recente de load sem colocá-la
  // nas deps da subscription — evita destroy/recreate do channel a cada render.
  const loadRef = useRef(load)
  useEffect(() => { loadRef.current = load }, [load])

  useEffect(() => { load() }, [load])

  // Realtime subscription — deps só em user.id para não recriar o channel
  // toda vez que load é redefinida.
  useEffect(() => {
    if (!user) return
    const channel = supabase
      .channel(`assinatura-notifs-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'assinatura_notificacoes',
        filter: `user_id=eq.${user.id}`,
      }, () => loadRef.current())
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const marcarLida = useCallback(async (id: string) => {
    try {
      const { error } = await supabase.from('assinatura_notificacoes')
        .update({ lida: true }).eq('id', id).eq('company_id', user?.company_id)
      if (error) console.error('[Assinaturas] Erro ao marcar notificação como lida:', error)
    } catch (e) { console.error('[Assinaturas] Erro inesperado:', e) }
    load()
  }, [load, user?.company_id])

  const marcarTodasLidas = useCallback(async () => {
    if (!user) return
    try {
      const { error } = await supabase.from('assinatura_notificacoes')
        .update({ lida: true }).eq('user_id', user.id).eq('lida', false).eq('company_id', user.company_id)
      if (error) console.error('[Assinaturas] Erro ao marcar todas como lidas:', error)
    } catch (e) { console.error('[Assinaturas] Erro inesperado:', e) }
    load()
  }, [user, load])

  return { notificacoes, count, load, marcarLida, marcarTodasLidas }
}
